/**
 * Reports uncaught errors to the backend, a few per session, and the protocol
 * mismatch found at boot. Legacy installed the window hook in UI.setup and
 * posted the protocol reports from Games.
 */
import { ajaxPost } from './Http';
import { GAME_STATE_CODES, session } from './Session';
import { version } from './Version';

/** Reports per session: an error raised every frame would otherwise post every frame. */
const MAX_ERROR_REPORTS_PER_SESSION = 5;

/** How many errors have been reported this session. */
let clientErrorCount = 0;

/** The report on the wire, under legacy's names for window.onerror's five arguments. */
interface ClientErrorReport {
  msg: string;
  url: string | undefined;
  lineNo: number | undefined;
  columnNo: number | undefined;
  /** The thrown value, already serialised so an Error's non-enumerable fields survive the outer stringify. */
  error: string | undefined;
}

/**
 * Installs the hooks: uncaught errors and unhandled promise rejections. The
 * network worker's errors arrive through Network, which owns the worker.
 */
export function setup(): void {
  window.addEventListener('error', reportErrorEvent);
  window.addEventListener('unhandledrejection', (event) => {
    reportRuntimeError('Unhandled promise rejection', undefined, undefined, undefined, event.reason);
  });
}

/** Reports an uncaught error, from the window or the network worker. */
export function reportErrorEvent(event: ErrorEvent): void {
  reportRuntimeError(event.message, event.filename, event.lineno, event.colno, event.error);
}

/** Reports the boot-time protocol mismatch, or the retry after the reload that followed. The promise never rejects. */
export function reportProtocolMismatch(retry: boolean): Promise<void> {
  return ajaxPost('/clienterror', { type: retry ? 'protocolretry' : 'protocol' }).then(
    () => undefined,
    () => undefined,
  );
}

/**
 * JSON.stringify replacer that turns an Error into a plain object: its name,
 * which lives on the prototype, and every own property, message and stack
 * included, which are not enumerable. Nested causes go through it too.
 */
function jsonErrorReplacer(_key: string, value: unknown): unknown {
  if (!(value instanceof Error)) {
    return value;
  }
  const plain: Record<string, unknown> = { name: value.name };
  for (const property of Object.getOwnPropertyNames(value)) {
    plain[property] = Reflect.get(value, property);
  }
  return plain;
}

/** Posts one runtime report while playing or connecting, as legacy did, up to the cap. */
function reportRuntimeError(
  message: string,
  url: string | undefined,
  lineNo: number | undefined,
  columnNo: number | undefined,
  thrown: unknown,
): void {
  if (session.state !== GAME_STATE_CODES.PLAYING && session.state !== GAME_STATE_CODES.CONNECTING) {
    return;
  }
  clientErrorCount += 1;
  if (clientErrorCount > MAX_ERROR_REPORTS_PER_SESSION) {
    return;
  }

  // The thrown value is serialised with the replacer; the report around it
  // goes through a plain stringify, as legacy's did.
  const report: ClientErrorReport = {
    msg: message,
    url,
    lineNo,
    columnNo,
    error: thrown != null ? JSON.stringify(thrown, jsonErrorReplacer) : undefined,
  };

  ajaxPost('/clienterror', {
    type: 'runtime',
    error: JSON.stringify(report),
    version,
    state: session.state,
    server: session.serverId,
  }).catch(() => {
    // A failed report must not raise another
  });
}
